import api from "api";
import ApiCaller from "api/apiCaller";
import Loading from "components/Loading";
import ModalCreateContract from "components/ModalCreateContract";
import ModalTerminate from "components/ModalTerminate";
import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useRouteMatch } from "react-router-dom";
import { Container } from "reactstrap";
import { Button } from "shared";
import { LISA } from "types";
import t from "utils/Lang";
import { getContractLocation } from "utils/SiteHelper";
import ContractTable from "./ContractTable";
import "./index.scss";

const Contract = () => {
  const match = useRouteMatch("/contract/:site_id");
  const params: any = match?.params;
  const siteId = params?.site_id;
  const [terminateReason, setTerminateReason] = useState([]);
  const [isOpenTerminate, setOpenTerminate] = useState(false);
  const [selectedContract, setSelectedContract] = useState<any>(null);
  const [isOpenCreateContract, setOpenCreateContract] = useState(false);

  const [contractTypes, setContractTypes] = useState<LISA.ContractTypeApiData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [siteData, setSiteData] = useState<LISA.SiteApiData | null>(null);
  const [contracts, setContracts] = useState<LISA.ContractApiData[]>([]);
  const [error, setError] = useState('');

  const toggleTerminate = useCallback(() => setOpenTerminate(!isOpenTerminate), [isOpenTerminate]);
  const toggle = useCallback(() => setOpenCreateContract(!isOpenCreateContract), [isOpenCreateContract]);

  const history = useHistory();

  const getTerminateReason = useCallback(async () => {
    const res = await ApiCaller.get("contract_termination_reason/");
    if (res.statusCode === 200) {
      setTerminateReason(res.result);
    }
  }, []);

  const getContractTypes = useCallback(async () => {
    const contractTypeRes = await ApiCaller.get("contract_type/");
    if (contractTypeRes.statusCode === 200) {
      setContractTypes(contractTypeRes.result);
    }
  }, []);

  const fetchSiteData = useCallback(async () => {
    try {
      const siteResp = await api.getSiteDetail(siteId, true);
      setSiteData(siteResp.result);
    } catch (e) {
      setSiteData(null);
      setError(e.message);
    }
  }, [siteId]);

  useEffect(() => {
    setIsLoading(true);

    getTerminateReason();
    getContractTypes();
    fetchSiteData();

    setIsLoading(false);
  }, [getContractTypes, fetchSiteData, getTerminateReason]);

  useEffect(() => {
    if (!siteData?.id) {
      setContracts([])
      return
    }

    api.getSiteContract(siteData?.id)
      .then(resp => {
        setContracts(resp.result.contracts);
      })
  }, [siteData?.id]);

  const onTerminate = useCallback((contract: any) => {
    setSelectedContract(contract);
    toggleTerminate();
  }, [toggleTerminate]);

  const onDelete = useCallback(async (contract: LISA.ContractApiData) => {
    try {
      await ApiCaller.delete(`contract/${contract.id}`);
      setContracts(currentContracts => [...currentContracts.filter(currentContract => currentContract.id !== contract.id)]);
    } catch (e) {
      alert(e.message);
    }    
  }, []);

  const onDone = useCallback(async (contract: any) => {
    try {
      await ApiCaller.put(`contract/${contract.id}`, contract);
    } catch (e) {
      alert(e.message);
    }
  }, []);

  const uploadAttachment = useCallback(async (id: number, files: Array<any>) => {
    if (!id || files.length === 0) return;
    const req: Array<any> = [];
    files.forEach((f) => {
      const attachmentFormData = new FormData();
      attachmentFormData.append("user_file", f.file);
      attachmentFormData.append(
        "attachment_content_relation_type",
        f.attribute
      );
      req.push(ApiCaller.post(`attachment/contract/${id}`, attachmentFormData));
      if (f.prevId) {
        req.push(ApiCaller.delete(`attachment/${f.prevId}`));
      }
    });
    await Promise.all(req);
  }, []);

  const onApply = useCallback(async (data: any, paymentTerms: Array<any>, taxData: any) => {
    data.deposit_fee = data.deposit_fee ? parseInt(data.deposit_fee) : 0;
    data.payment_period_monthly_cycle = data.payment_period_monthly_cycle
      ? parseInt(data.payment_period_monthly_cycle)
      : 0;

    const newContracts = [...contracts];
    const paymentTermsBody = paymentTerms.map((el, index) => {
      if (index === 0) {
        el.increase_fee_rate = null;
      }
      el.revenue_share_percentage = el.revenue_share_percentage
        ? parseInt(el.revenue_share_percentage) / 100
        : null;
      return el;
    });

    const contract_tax: any = {
      valid_from: data.available_from,
      valid_to: data.available_to,
      tax_percentage: taxData.tax_percentage / 100,
      contract_tax_type_id: taxData.contract_tax_type_id,
    };
    const files = paymentTerms
      .map((el, index) => ({
        attribute: `payment_file_${index}`,
        file: el.payment_file?.file,
        prevId: el.payment_file?.prevId,
      }))
      .filter((el) => !!el.file);
    if (data.contract_file?.file) {
      files.push({
        attribute: "contract_file",
        file: data.contract_file?.file,
        prevId: data.contract_file?.prevId,
      });
    }
    if (data.action === "create") {
      const res = await ApiCaller.post("contract", {
        ...data,
        site_id: siteId,
      });
      await uploadAttachment(res.result.id, files);
      if (res.status === "ok") {
        data.id = res.result.id;
        const taxRes = await ApiCaller.post(
          `contract/${res.result.id}/contract_tax`,
          {
            contract_taxes: [contract_tax],
          }
        );
        await ApiCaller.post(`contract/${res.result.id}/payment_term`, {
          payment_terms: paymentTermsBody,
        });
        contract_tax.id = taxRes?.result?.id;
        data.contract_taxes = [contract_tax];
        data.payment_terms = paymentTermsBody;
        newContracts.push(data);
      }
    } else if (data.action === "edit") {
      await uploadAttachment(data.id, files);
      const res = await ApiCaller.put(`contract/${data.id}`, data);
      const taxRes = await ApiCaller.post(
        `contract/${res.result.id}/contract_tax`,
        {
          contract_taxes: [contract_tax],
        }
      );
      await ApiCaller.post(`contract/${data.id}/payment_term`, {
        payment_terms: paymentTermsBody,
      });
      if (res.status === "ok") {
        contract_tax.id = taxRes?.result?.id;
        const idx = newContracts.findIndex((ct: any) => ct.id === data.id);
        data.payment_terms = paymentTermsBody;
        data.contract_taxes = [contract_tax];
        newContracts[idx] = data;
      }
    } else if (data.action === "delete") {
      await ApiCaller.delete(`contract/${data.id}`);
      const idx = newContracts.findIndex((ct: any) => ct.id === data.id);
      newContracts[idx] = data;
    }

    setContracts(newContracts);
    toggle();
  }, [contracts, siteId, toggle, uploadAttachment]);

  const handleOnMarkAsRentalFee = useCallback(async (contract: LISA.ContractApiData) => {
    try {
      await ApiCaller.post(
        `contract/${contract.id}/used_for_rental_fee_calculation`
      );

      setContracts(currentContracts => {
        return [...currentContracts.map(currentContract => {
          if (currentContract.id === contract.id) {
            return {...currentContract, used_for_rental_fee_calculation: true}
          }

          return currentContract
        })]
      })

      alert("Success");
    } catch (e) {
      alert(e.message);
    }
  }, []);

  const handleContractTerminate = useCallback(async (reason, date) => {
    try {
      const resp = await api.terminateContract(selectedContract?.id, {
        contract_termination_reason_id: reason?.[0]?.value,
        terminated_on: date,
      });

      setContracts(currentContracts => {
        return [...currentContracts.map(currentContract => {
          if (currentContract.id === selectedContract?.id) {
            return {
              ...currentContract,
              contract_termination_reason: {
                name: reason[0].label,
                id: resp.result.id,
                code: '',
                is_active: true,
              } as any,
              is_terminated: true,
              terminated_on: date,
            }
          }

          return currentContract
        })]
      })

      alert("Terminated");
    } catch (e) {
      alert(e.message);
    }

    toggleTerminate();
  }, [selectedContract?.id, toggleTerminate]);

  const handleViewSiteClick = useCallback(() => {
    history.push(`/sitev2/${siteData?.id}`)
  }, [history, siteData?.id]);

  if (isLoading) {
    return <Loading />;
  }

  if (error) {
    return (
      <div className="ifrs-detail__container">
        <span>{error}</span>
      </div>
    );
  }

  return (
    <Container style={{ height: "100%" }}>
      <div className="contract__container">
        <div className="page-header">
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span className="page-title">{siteData?.name}</span>
            <span className="page-description">
              {t().site_listing__address}:{" "}
              <span className="page-description-value">
                {getContractLocation(siteData)}
              </span>
            </span>
          </div>
          <div className="d-flex align-items-center">
            <Button
              label={t().action__create_contract}
              icon="add"
              type="primary"
              onClick={toggle}
            />
            <Button label="Xem chi tiết MB" type="primary" customClassName="ml-2" onClick={handleViewSiteClick} />
          </div>
        </div>
        <ContractTable
          data={contracts}
          contractTypes={contractTypes}
          onMarkAsRentalFee={handleOnMarkAsRentalFee}
          onDelete={onDelete}
          onEdit={(contract) => {
            setSelectedContract(contract);
            toggle();
          }}
          onDone={onDone}
          onTerminate={onTerminate}
        />
      </div>
      <ModalCreateContract
        siteAddress={getContractLocation(siteData)}
        onClosed={() => setSelectedContract(null)}
        isOpen={isOpenCreateContract}
        toggle={toggle}
        onApply={onApply}
        contractId={selectedContract?.id}
        contracts={contracts}
      />
      <ModalTerminate
        isOpen={isOpenTerminate}
        toggle={toggleTerminate}
        contract={selectedContract}
        onTerminate={handleContractTerminate}
        reason={terminateReason}
      />
    </Container>
  );
};

export default Contract;
